import { BotIcon, BotOffIcon, UserRoundIcon } from 'lucide-react';

import type { ActorView } from '@/lib/api/generated/model';

/**
 * Renders an `ActorView` per FR-007/FR-F007: a human actor shows their name,
 * falling back to the raw identifier when none resolved — the same
 * name-then-id degradation the AI branch below already uses, and the reason the
 * identifier is still carried on the wire at all (spec 067 FR-021);
 * an AI actor shows workflow/step/task, each falling back from its `*_name`
 * to the matching raw id (`task_id`/`step_id`) when the name hasn't been
 * resolved (research.md R9). `ActorView` has no `workflow_id` — a workflow
 * with no resolved name has no raw id to fall back to, so it gets a generic
 * placeholder instead.
 *
 * The kind is an icon rather than the word `AI`/`HUMAN`, which is the field's
 * value rather than anything a reader asked to see.
 *
 * `detail="task"` names only the task an agent was running. The full form is
 * three names and two separators, which needs the width the history pane has;
 * in a narrow rail all three truncate at once and the line degrades into
 * "Capture Kille… › Write Then… › Te…", which identifies nothing. The task is
 * the one of the three that answers "what was this for", so it is the one that
 * survives. Both forms live here rather than in the caller so the two surfaces
 * cannot drift into naming actors differently.
 */
/** What an unattributed change is called (spec 143). */
export const UNATTRIBUTED_ACTOR = 'Another agent (not attributed)';

export function ActorLabel({
  actor,
  detail = 'full',
}: {
  actor: ActorView;
  detail?: 'full' | 'task';
}) {
  if (actor.kind === 'human') {
    return (
      <span className="inline-flex min-w-0 items-center gap-1">
        <UserRoundIcon
          aria-label="Person"
          className="size-3.5 shrink-0"
          strokeWidth={1.75}
        />
        <span className="truncate" title={actor.user_name ?? undefined}>
          {actor.user_name ?? actor.user_id ?? 'Unknown user'}
        </span>
      </span>
    );
  }

  if (actor.kind === 'unattributed') {
    // A change a run's capture saw while another task's run shared the
    // workspace, with nothing of its own naming it (service spec 126): an
    // agent did it, and which one cannot be said.
    return (
      <span className="text-muted-foreground inline-flex min-w-0 items-center gap-1">
        <BotOffIcon
          aria-hidden="true"
          className="size-3.5 shrink-0"
          strokeWidth={1.75}
        />
        <span className="truncate">{UNATTRIBUTED_ACTOR}</span>
      </span>
    );
  }

  const workflow = actor.workflow_name ?? 'Unknown workflow';
  const step = actor.step_name ?? actor.step_id ?? 'Unknown step';
  const task = actor.task_name ?? actor.task_id ?? 'Unknown task';

  if (detail === 'task') {
    return (
      <span
        className="inline-flex min-w-0 items-center gap-1"
        // The two names dropped from the visible line are still the answer to
        // "which run was this", so they stay reachable on the element itself.
        title={`${workflow} \u203a ${step} \u203a ${task}`}
      >
        <BotIcon
          aria-label="Agent"
          className="size-3.5 shrink-0"
          strokeWidth={1.75}
        />
        <span className="truncate">{task}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex min-w-0 items-center gap-1">
      <BotIcon
        aria-label="Agent"
        className="size-3.5 shrink-0"
        strokeWidth={1.75}
      />
      <span className="truncate">{workflow}</span>
      <span className="text-muted-foreground shrink-0" aria-hidden="true">
        &rsaquo;
      </span>
      <span className="truncate">{step}</span>
      <span className="text-muted-foreground shrink-0" aria-hidden="true">
        &rsaquo;
      </span>
      <span className="truncate">{task}</span>
    </span>
  );
}
